import React, { useState, useEffect, useCallback } from 'react';
import './TranslationAdjustment.scss';

interface TranslationAdjustmentProps {
    /** 是否显示下拉菜单 */
    showDropdown: boolean;
    /** 是否正在翻译中 */
    isTranslating: boolean;
    /** 下拉菜单显示状态变化回调 */
    onDropdownChange: (show: boolean) => void;
    /** 翻译操作回调 */
    onTranslate: (customText?: string) => void;
    /** 自定义文本 */
    customText?: string;
    /** 自定义文本变化回调 */
    onCustomTextChange?: (text: string) => void;
    /** 按钮文本 */
    buttonText?: string;
    /** 按钮样式类名 */
    buttonClassName?: string;
    /** 下拉菜单位置偏移 */
    dropdownOffset?: { top?: string; left?: string };
    /** 自定义卡片位置偏移 */
    customCardOffset?: { top?: string; left?: string };
}

const TranslationAdjustment: React.FC<TranslationAdjustmentProps> = ({
    showDropdown,
    isTranslating,
    onDropdownChange,
    onTranslate,
    customText = '',
    onCustomTextChange,
    buttonText = '调整翻译',
    buttonClassName = 'adjust-translate-btn',
    dropdownOffset = { top: '100%', left: '0' },
    customCardOffset = { top: '100%', left: '120px' }
}) => {
    const [showCustomCard, setShowCustomCard] = useState(false);
    const [localCustomText, setLocalCustomText] = useState(customText);

    // 同步外部customText到内部状态
    useEffect(() => {
        setLocalCustomText(customText);
    }, [customText]);

    // 处理调整翻译选项
    const handleAdjustOption = useCallback((option: string) => {
        console.log(`选择调整翻译选项: ${option}`);
        
        switch (option) {
            case 'longer':
                // 立即关闭下拉框
                onDropdownChange(false);
                onTranslate('更加长一些');
                break;
            case 'shorter':
                // 立即关闭下拉框
                onDropdownChange(false);
                onTranslate('更加短一些');
                break;
            case 'custom':
                if (showCustomCard) {
                    // 如果卡片已显示，则隐藏卡片但保持下拉框显示
                    setShowCustomCard(false);
                } else {
                    // 如果卡片未显示，则显示卡片并保持下拉框显示
                    setShowCustomCard(true);
                }
                break;
        }
    }, [showCustomCard, onDropdownChange, onTranslate]);

    // 自定义翻译处理函数
    const handleCustomRetranslate = useCallback(() => {
        // 如果自定义文本为空，不触发翻译接口
        if (!localCustomText || !localCustomText.trim()) {
            return;
        }
        // 立即关闭下拉框和卡片
        onDropdownChange(false);
        setShowCustomCard(false);
        onTranslate(localCustomText);
    }, [localCustomText, onDropdownChange, onTranslate]);

    // 处理自定义文本变化
    const handleCustomTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value;
        setLocalCustomText(newText);
        if (onCustomTextChange) {
            onCustomTextChange(newText);
        }
    }, [onCustomTextChange]);

    // 点击外部关闭下拉菜单
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            
            // 检查是否点击了翻译调整容器
            const translationAdjustmentContainer = document.querySelector('.translation-adjustment-container');
            const clickedContainer = translationAdjustmentContainer && translationAdjustmentContainer.contains(target);
            
            // 检查是否点击了自定义翻译卡片
            const customCard = document.querySelector('.custom-translation-card');
            const clickedCustomCard = customCard && customCard.contains(target);
            
            // 如果点击了外部区域（不是翻译调整容器或自定义卡片），则关闭下拉框和卡片
            if (showDropdown && !clickedContainer && !clickedCustomCard) {
                onDropdownChange(false);
                setShowCustomCard(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown, onDropdownChange]);

    return (
        <div className="translation-adjustment-container">
            {/* 调整翻译下拉菜单 */}
            {showDropdown && (
                <div 
                    className="adjust-translation-dropdown"
                    style={{
                        position: 'absolute',
                        top: dropdownOffset.top,
                        left: dropdownOffset.left,
                        zIndex: 1001,
                        marginTop: '2px' // 添加2px间距，更靠近按钮
                    }}
                >
                    <div className="dropdown-content">
                        <div
                            className={`dropdown-item ${isTranslating ? 'disabled' : ''}`}
                            onClick={(e) => {
                                if (isTranslating) return;
                                e.stopPropagation();
                                handleAdjustOption('longer');
                            }}
                            style={{ 
                                opacity: isTranslating ? 0.6 : 1,
                                cursor: isTranslating ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isTranslating ? '翻译中...' : '更加长一些'}
                        </div>
                        <div
                            className={`dropdown-item ${isTranslating ? 'disabled' : ''}`}
                            onClick={(e) => {
                                if (isTranslating) return;
                                e.stopPropagation();
                                handleAdjustOption('shorter');
                            }}
                            style={{ 
                                opacity: isTranslating ? 0.6 : 1,
                                cursor: isTranslating ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isTranslating ? '翻译中...' : '更加短一些'}
                        </div>
                        <div 
                            className="dropdown-item" 
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAdjustOption('custom');
                            }}
                        >
                            <i className="fas fa-edit"></i>
                            自定义
                            <i className="fas fa-chevron-right"></i>
                        </div>
                    </div>
                </div>
            )}

            {/* 自定义翻译卡片 */}
            {showCustomCard && showDropdown && (
                <div 
                    className="custom-translation-card"
                    style={{
                        position: 'absolute',
                        top: customCardOffset.top,
                        left: customCardOffset.left,
                        zIndex: 1001
                    }}
                >
                    <div className="custom-card-content">
                        <textarea
                            className="custom-textarea"
                            placeholder="请输入你的翻译偏好或要求"
                            value={localCustomText}
                            maxLength={700}
                            spellCheck={false}
                            onChange={handleCustomTextChange}
                        />
                        <div className="custom-card-footer">
                            <span className="char-counter">{localCustomText.length} / 700</span>
                            <button 
                                className="retranslate-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCustomRetranslate();
                                }}
                                disabled={isTranslating || !localCustomText || !localCustomText.trim()}
                            >
                                {isTranslating ? '翻译中...' : '重新翻译'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TranslationAdjustment;





